import "react-data-grid/lib/styles.css";
import DataGrid from "react-data-grid";
import { useEffect, useMemo, useState } from "react";
import { Icon } from "../../tremor/Icon";
import { Title } from "../../tremor/Text";
import { FunnelIcon as FunnelIconOutline } from "@heroicons/react/24/outline";
import { FunnelIcon as FunnelIconSolid } from "@heroicons/react/24/solid";
import { BarsArrowDownIcon, BarsArrowUpIcon } from "@heroicons/react/20/solid";
import React from "react";
import { MultiSelect } from "../../tremor/MultiSelect";
import { useTheme } from "../../layouts/Dashboard/useTheme";
import { useBackend } from "../../layouts/Wrapper";
function urlify(text) {
    const markdownImageRegex = /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g;
    const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    let text1 = text + "";
    text1 = text1.replace(markdownImageRegex, (_, altText, url) => {
        let properUrl = url.startsWith("http") ? url : "https://" + url;
        return `<img style="height:28px;width:28px;object-fit:contain;object-position:50% 50%;" src="${properUrl}" alt="${altText}" />`;
    });
    text1 = text1.replace(markdownLinkRegex, (_, linkText, url) => {
        let properUrl = url.startsWith("http") ? url : "https://" + url;
        return `<a target="_blank" href="${properUrl}">${linkText}</a>`;
    });
    if (text1 !== text) {
        return text1;
    }
    return text1.replace(urlRegex, (url) => {
        let properUrl = url.startsWith("http") ? url : "https://" + url;
        return `<a target="_blank" href="${properUrl}">${url}</a>`;
    });
}
const cellRenderer = (obj) => {
    let content = urlify(obj.row[obj.column.key]);
    return React.createElement("span", { dangerouslySetInnerHTML: { __html: content } });
};
export const TableChart = ({ data, pagination, setPagination, padding, }) => {
    const [filterEnabled, setFilterEnabled] = useState(false);
    const [options, setOptions] = useState({});
    const [filters, setFilters] = useState({});
    const theme = useTheme();
    const { containerRef } = useBackend();
    let [allObjects, fields] = useMemo(() => {
        var _a, _b, _c, _d;
        let newRows = [];
        let label = ((_d = (_c = (_b = (_a = data.options) === null || _a === void 0 ? void 0 : _a.scales) === null || _b === void 0 ? void 0 : _b.x) === null || _c === void 0 ? void 0 : _c.title) === null || _d === void 0 ? void 0 : _d.text) || "Label";
        data.data.datasets[0].data.map((item, i) => {
            let row = {};
            if (data.data.labels && data.data.labels[i]) {
                row = {
                    [label]: data.data.labels[i],
                };
            }
            data.data.datasets.map((a, j) => {
                row[a.label] = a.data[i];
            });
            newRows.push(row);
        });
        if (data.data.labels) {
            let newFields = [
                label,
                ...data.data.datasets.map((a) => a.label),
            ];
            return [newRows, newFields];
        }
        else {
            let newFields = data.data.datasets.map((a) => a.label);
            return [newRows, newFields];
        }
    }, [data]);
    let objects = useMemo(() => {
        let newRows = [...allObjects];
        // Filter newRows based on filters
        if (filters) {
            newRows = newRows.filter((row) => {
                let pass = true;
                Object.keys(filters).forEach((key) => {
                    if (filters[key].length > 0 && !filters[key].includes(row[key])) {
                        pass = false;
                    }
                });
                return pass;
            });
        }
        // Sort newRows here based on pagination data
        if (pagination) {
            const { sortColumn, sortDirection } = pagination;
            if (sortColumn && sortDirection) {
                const parseNumeric = (value) => {
                    if (value === "" || value == null)
                        return null;
                    // Strip formatting characters (commas, currency symbols, spaces, %)
                    const cleaned = String(value).replace(/[,\s$€£¥%]/g, "");
                    const num = Number(cleaned);
                    return isNaN(num) ? null : num;
                };
                newRows.sort((a, b) => {
                    const valueA = a[sortColumn];
                    const valueB = b[sortColumn];
                    const numA = parseNumeric(valueA);
                    const numB = parseNumeric(valueB);
                    const isNumeric = numA !== null && numB !== null;
                    const comparison = isNumeric
                        ? numA - numB
                        : valueA < valueB
                            ? -1
                            : valueA > valueB
                                ? 1
                                : 0;
                    return sortDirection === "ASC" ? comparison : -comparison;
                });
            }
        }
        return newRows;
    }, [allObjects, filters, pagination]);
    useEffect(() => {
        let newOptions = {};
        fields.forEach((field) => {
            newOptions[field] = [];
            allObjects.forEach((row) => {
                if (!newOptions[field].includes(row[field])) {
                    newOptions[field].push(row[field]);
                }
            });
            newOptions[field].sort();
        });
        setOptions(newOptions);
    }, [allObjects, fields]);
    const handleSortChange = (newSorting) => {
        if (!setPagination || !pagination) {
            return;
        }
        if (newSorting.length > 0) {
            const { columnKey, direction } = newSorting[0];
            setPagination(Object.assign(Object.assign({}, pagination), { sortColumn: columnKey, sortDirection: direction, currentPage: 1 }));
        }
        else {
            setPagination(Object.assign(Object.assign({}, pagination), { sortColumn: undefined, sortDirection: undefined }));
        }
    };
    const columns = useMemo(() => {
        return Object.keys(options).map((a) => ({
            key: a,
            name: a,
            renderCell: cellRenderer,
            renderHeaderCell: (p) => {
                const isMatchingFilterColumn = (pagination === null || pagination === void 0 ? void 0 : pagination.sortColumn) === p.column.name;
                const filterTypeClass = isMatchingFilterColumn
                    ? "onvo-table-widget-filter-active"
                    : "onvo-table-widget-filter-inactive";
                return (React.createElement("div", { className: "onvo-table-widget-header onvo-flex onvo-h-full onvo-flex-col onvo-justify-center onvo-gap-2", style: { lineHeight: "12px" } },
                    React.createElement("div", { className: "onvo-table-widget-header-title onvo-flex onvo-flex-row onvo-items-center onvo-justify-between" },
                        p.column.name,
                        React.createElement("div", { className: `${filterTypeClass} onvo-flex onvo-flex-row` },
                            isMatchingFilterColumn && (React.createElement(Icon, { icon: (pagination === null || pagination === void 0 ? void 0 : pagination.sortDirection) === "ASC"
                                    ? BarsArrowUpIcon
                                    : BarsArrowDownIcon, size: "sm" })),
                            React.createElement(Icon, { onClick: (e) => {
                                    setFilterEnabled((a) => !a);
                                    e.preventDefault();
                                    e.stopPropagation();
                                }, icon: filterEnabled ? FunnelIconSolid : FunnelIconOutline, size: "sm" }))),
                    filterEnabled && (React.createElement(MultiSelect, { container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, placeholder: "Filter", className: "-onvo-mt-2", onValueChange: (val) => {
                            setFilters(Object.assign(Object.assign({}, filters), { [p.column.key]: val }));
                        }, items: options[a].map((c) => ({
                            label: c,
                            value: c,
                        })) }))));
            },
        }));
    }, [options, filterEnabled, pagination]);
    const sortColumns = useMemo(() => {
        if (pagination && pagination.sortColumn) {
            return [{ columnKey: pagination.sortColumn, direction: pagination.sortDirection || "ASC" }];
        }
        return [];
    }, [pagination]);
    return (React.createElement("div", { style: { padding: (padding) + "px" }, className: "onvo-table-widget onvo-h-full onvo-flex onvo-flex-col onvo-relative" },
        React.createElement(Title, { className: "onvo-table-widget-title onvo-text-md onvo-font-override onvo-my-0" }, data.options.plugins.title.text),
        React.createElement(DataGrid, { className: "onvo-table-widget-data-grid onvo-fill-grid onvo-mt-3 onvo-h-full onvo-rounded-xl onvo-border-solid onvo-border onvo-border-gray-200 dark:onvo-border-gray-800 " +
                (theme === "dark" ? "rdg-dark" : "rdg-light"), defaultColumnOptions: {
                sortable: true,
                resizable: true,
            }, enableVirtualization: true, headerRowHeight: filterEnabled ? 80 : 35, onSortColumnsChange: handleSortChange, sortColumns: sortColumns, columns: columns, rows: objects })));
};
//# sourceMappingURL=TableChart.js.map